import { ElasticProduct, ElasticVariant } from "@usereactify/search";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useIsMobile } from "../../hooks/useIsMobile";
//import { Product } from "@usereactify/search/dist/src/types/graphql";

interface CustomObject {
  [key: string]: string;
}
interface ProductImage {
  variant_ids: number[];
  src: string;
}
interface ProductData {
  product: {
    images: ProductImage[];
  };
}

type ResultsSwatchesWithShopifyVariantProps = {
  product: ElasticProduct;
  setSwatchImage: React.Dispatch<React.SetStateAction<string | null | undefined>>;
  handleVariantChangeBasedOnColorOption: (variant: ElasticVariant) => void;
  tags: string[];
};

export const ResultsSwatchesWithShopifyVariant: React.FunctionComponent<ResultsSwatchesWithShopifyVariantProps> = (
  props
) => {
  const { product, setSwatchImage, handleVariantChangeBasedOnColorOption, tags } = props;
  const [swatchIndex, setSwatchIndex] = useState<number>(0);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const ref = useRef(null);
  const { isMobile } = useIsMobile();
  const pageUrl = window.location.pathname;

  let totalHiddenSwatches = 0;

  useEffect(() => {
    setSwatchIndex(0);
  }, [totalHiddenSwatches]);

  // check if product.options[] have any 'color' or 'colour' :
  let hasColor = false;
  let colorOptionIndex = 0;
  product.options?.forEach((option, index) => {
    if (option === "Color" || option === "Colour" || option === "color" || option === "colour") {
      hasColor = true;
      colorOptionIndex = index;
    }
  });
  if (!hasColor) return null;

  // To fix the variant.id is null issue, we need to get the variant.id from the storefrontId:
  const getVariantId = (variant: ElasticVariant) => {
    return parseInt(variant.storefrontId.split("/").pop() || "");
  };

  // we need to make sure one color only has one variant:
  const { colors, colorVariants } = useMemo(() => {
    let colors: string[] = [];
    let colorVariants: Array<ElasticVariant> = [];

    product.variants?.forEach((variant) => {
      // check option1, option2, option3 based on colorOptionIndex:
      switch (colorOptionIndex) {
        case 0:
          if (variant.option1 && !colors.includes(variant.option1)) {
            colors.push(variant.option1);
            colorVariants.push(variant);
          }
          break;
        case 1:
          if (variant.option2 && !colors.includes(variant.option2)) {
            colors.push(variant.option2);
            colorVariants.push(variant);
          }
          break;
        case 2:
          if (variant.option3 && !colors.includes(variant.option3)) {
            colors.push(variant.option3);
            colorVariants.push(variant);
          }
          break;
        default:
          break;
      }
    });

    return { colors, colorVariants };
  }, [product.variants, colorOptionIndex]);

  const swiper = useRef<Swiper | null>(null);
  const trySwiperInit = () => {
    // check if we have Swiper on the page:
    if (typeof (window as any).Swiper !== "undefined") {
      swiper.current = new (window as any).Swiper(ref.current, {
        slidesPerView: "auto",
        spaceBetween: 4,
        breakpoints: {
          768: {
            spaceBetween: 8,
          },
        },
      });
    } else {
      swiper.current = null;
      setTimeout(() => {
        trySwiperInit();
      }, 1000);
    }
  };
  const [swiperIsLocked, setSwiperIsLocked] = useState<boolean>(true);
  const handleResize = () => {
    if (swiper.current) {
      if (swiper.current.isLocked) {
        setSwiperIsLocked(true);
      } else {
        setSwiperIsLocked(false);
      }
    }
  };
  const [error, setError] = useState<string | null>(null);

  // use the product handle to fetch from Shopify:
  // https://remarkable-outdoor-living.myshopify.com/products/soho-3-seater-sofa
  const [variantImages, setVariantImages] = useState<CustomObject>({});
  useEffect(() => {
    const fetchVariantImages = async () => {
      if (!colorVariants?.length || !product?.variants?.length) {
        return;
      }

      try {
        const response = await fetch(`https://remarkable-outdoor-living.myshopify.com/products/${product.handle}.json`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data: ProductData = await response.json();

        if (!data.product?.images) {
          throw new Error("Product images not found in the response");
        }

        let tempVariantImages: CustomObject = {};
        data.product.images.forEach((image: ProductImage) => {
          colorVariants.forEach((variant) => {
            const variantId = getVariantId(variant);
            if (variantId && image.variant_ids.includes(variantId)) {
              tempVariantImages[variantId] = image.src;
            }
          });
        });

        setVariantImages(tempVariantImages);
      } catch (err) {
        console.error("Error fetching variant images:", err);
        setError(err instanceof Error ? err.message : "An unknown error occurred");
      }
    };

    fetchVariantImages();

    const handleResize = () => {
      if (swiper.current) {
        setSwiperIsLocked(!!swiper.current.isLocked);
      }
    };

    trySwiperInit();
    window.addEventListener("resize", handleResize);
    handleResize(); // initial check

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [product, colorVariants]);

  if (error) {
    return <div>Error loading variant images: {error}</div>;
  }

  const getBackgroundImages = (possibleFilename: string) => {
    if (!possibleFilename)
      return {
        backgroundColor: "transparent",
      } as React.CSSProperties;

    // example url:  https://remarkable-outdoor-living.myshopify.com/cdn/shop/files/anthracite_50x50.png
    // use multiple url try to find the image on Shopify:
    let filename = possibleFilename.replace(/\s/g, "-"); // replace all space with dash
    //const normalBackgroundImage = `url("https://remarkable-outdoor-living.myshopify.com/cdn/shop/files/${filename}_50x50.png"), url("https://remarkable-outdoor-living.myshopify.com/cdn/shop/files/${filename}.png")`;
    // all lower case:
    filename = filename.toLowerCase();
    const allLowerCaseBackgroundImage = `url("https://remarkable-outdoor-living.myshopify.com/cdn/shop/files/${filename}_50x50.png"), url("https://remarkable-outdoor-living.myshopify.com/cdn/shop/files/${filename}.png")`;
    // First letter upper case:
    // filename = filename.replace(/^\w/, (c) => c.toUpperCase());
    // const firstLetterUpperCaseBackgroundImage = `url("https://remarkable-outdoor-living.myshopify.com/cdn/shop/files/${filename}_50x50.png"), url("https://remarkable-outdoor-living.myshopify.com/cdn/shop/files/${filename}.png")`;
    return {
      backgroundImage: `${allLowerCaseBackgroundImage}`,
      // backgroundImage: `${normalBackgroundImage}, ${allLowerCaseBackgroundImage}, ${firstLetterUpperCaseBackgroundImage}`,
      //backgroundImage: `${normalBackgroundImage}`,
      backgroundColor: possibleFilename,
    } as React.CSSProperties;
  };

  return (
    <div className="result-swatch-swiper-wrapper">
      <div
        ref={ref}
        className={`swiper ${isVisible ? "opacity-100" : "opacity-0"}`}
        style={{
          width: "100%",
        }}
      >
        <div
          className="grid-product__colors-disable swiper-wrapper"
          style={
            {
              // overflow: `hidden`,
              //width: `${swatchGroupWidth}px`,
              // flexWrap: `nowrap`,
            }
          }
        >
          {colorVariants?.map((variant, index) => (
            <button
              type="button"
              className="swiper-slide result-swatch-link"
              style={{
                width: "fit-content",
              }}
              onClick={() => {
                setSwatchImage(variantImages[getVariantId(variant)]);
                handleVariantChangeBasedOnColorOption(variant);
              }}
              onMouseOver={() => {
                setSwatchImage(variantImages[getVariantId(variant)]);
                handleVariantChangeBasedOnColorOption(variant);
              }}
              onMouseLeave={() => {
                if (isMobile) return;
                setSwatchImage(null);
                handleVariantChangeBasedOnColorOption(colorVariants[0]);
              }}
            >
              <span className={`result-color-swatch`}>
                <span
                  className="result-color-swatch__inner"
                  data-color={colors[index]}
                  style={getBackgroundImages(colors[index])}
                ></span>
              </span>
            </button>
          ))}
        </div>
        {/* {totalHiddenSwatches > 0 && (
          <>
            <p className="color-swatch__more small">+{totalHiddenSwatches} more</p>
          </>
        )} */}
      </div>

      {swiperIsLocked === false && (
        <svg
          className="result-swiper-right-arrow"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          onClick={() => {
            if (!swiper.current) return;
            // if we are at the last slide, go back to the first slide:
            if (swiper.current.isEnd) swiper.current.slideTo(0);
            else {
              // find the next out of view slide and slide to it:
              let nextOutOfViewSlideIndex = 0;
              // use getBoundingClientRect() to find the next out of view slide:
              const slides = swiper.current.slides;
              const rightEdge = swiper.current.el.getBoundingClientRect().right;
              for (let i = 0; i < slides.length; i++) {
                if (slides[i].getBoundingClientRect().right > rightEdge) {
                  nextOutOfViewSlideIndex = i;
                  break;
                }
              }
              swiper.current.slideTo(nextOutOfViewSlideIndex);
            }
            // else swiper.current.slideNext();
          }}
        >
          <path d="M4.57157 1.14285L11.4287 8L4.57157 14.8571" stroke="black" stroke-width="2" />
        </svg>
      )}
    </div>
  );
};
